'use client';

import { Card, Label } from '@/components/ui';
import { DiscreteDistributionGraph, ContinuousDistributionGraph } from '../common/distribution-graph';
import { ScrollableContainer } from '../common/scrollable-container';

export interface NormalResult {
  pdf: number[];
  cdf: number[];
  xValues: number[];
  mean: number;
  standardDeviation: number;
  zScore: number;
  probabilityLessThan: number;  // P(X < x)
  probabilityGreaterThan: number;  // P(X > x)
  value: number;
}

export interface NormalCalculatorProps {
  visualizationData: NormalResult | null;
}

export function NormalCalculator({ visualizationData: result }: NormalCalculatorProps) {
  if (!result) {
    return null;
  }

  // Debug logging
  console.log('CDF Data:', {
    cdf: result.cdf,
    xValues: result.xValues,
    dataLength: result.cdf?.length,
    xValuesLength: result.xValues?.length,
    // Add sample values to check data
    firstFewCDF: result.cdf?.slice(0, 5),
    lastFewCDF: result.cdf?.slice(-5),
    probabilityLessThan: result.probabilityLessThan,
    mean: result.mean,
    standardDeviation: result.standardDeviation
  });

  return (
    <ScrollableContainer>
      <div className="space-y-6">
        <Card className="p-4 rounded-lg">
          <h3 className="text-lg font-semibold mb-4">Normal Distribution Results</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Z-Score</Label>
              <div>{result.zScore.toFixed(4)}</div>
            </div>
            <div>
              <Label>Value (x)</Label>
              <div>{result.value.toFixed(2)}</div>
            </div>
            <div>
              <Label>P(X &lt; {result.value.toFixed(2)})</Label>
              <div>{(result.probabilityLessThan * 100).toFixed(2)}%</div>
            </div>
            <div>
              <Label>P(X &gt; {result.value.toFixed(2)})</Label>
              <div>{(result.probabilityGreaterThan * 100).toFixed(2)}%</div>
            </div>
          </div>
        </Card>

        <Card className="p-4 rounded-lg">
          <h3 className="text-lg font-semibold mb-4">Probability Density Function (PDF)</h3>
          <DiscreteDistributionGraph
            data={result.pdf}
            title={`Normal Distribution (μ=${result.mean}, σ=${result.standardDeviation})`}
            xAxisLabel="Value"
            yAxisLabel="Probability Density"
            height={300}
            xAxisValues={result.xValues}
            showMean
            showMedian
            showStdDev
            labels={[
              {
                value: result.mean,
                text: `μ = ${result.mean.toFixed(2)}`,
                color: '#ff4d4d',
                lineStyle: 'solid'
              },
              {
                value: result.value,
                text: `x = ${result.value.toFixed(2)}`,
                color: '#4d4dff',
                lineStyle: 'solid'
              },
              {
                value: result.mean - result.standardDeviation,
                text: `-1σ`,
                color: '#666666',
                lineStyle: 'dashed'
              },
              {
                value: result.mean + result.standardDeviation,
                text: `+1σ`,
                color: '#666666',
                lineStyle: 'dashed'
              }
            ]}
          />
        </Card>

        <Card className="p-4 rounded-lg">
          <h3 className="text-lg font-semibold mb-4">Cumulative Distribution Function (CDF)</h3>
          <ContinuousDistributionGraph
            data={result.cdf}
            title={`Cumulative Distribution (μ=${result.mean}, σ=${result.standardDeviation})`}
            xAxisLabel="Value"
            yAxisLabel="Cumulative Probability"
            height={300}
            xAxisValues={result.xValues}
            showMean
            labels={[
              {
                value: result.mean,
                text: `μ = ${result.mean.toFixed(2)}`,
                color: '#ff4d4d',
                lineStyle: 'solid'
              },
              {
                value: result.value,
                text: `x = ${result.value.toFixed(2)}, P(X ≤ x) = ${(result.probabilityLessThan * 100).toFixed(2)}%`,
                color: '#4d4dff',
                lineStyle: 'solid'
              }
            ]}
          />
        </Card>
      </div>
    </ScrollableContainer>
  );
} 